import classNames from "classnames";
import React, { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import coverImage from "../../../../assets/images/misc/course-cover.webp";
import { Button } from "../../../button/Button";
import { Icon } from "../../../graphic/Icon";
import { Spinner } from "../../../loaders/Spinner";
import Image from "../../../media/Image";
import { TextReveal } from "../../../typography/TextReveal";
import styles from "./CourseCard.scss";

const msgKey = "courseCard";

/**
 * A course card: an image, a text (with an optional intro before it, never on its own) and/or a question with options
 * @param coverUrl – course cover, shown blurred behind cards without an image of their own
 * @param questionNumber / questionCount – shown as "Question 2 of 5" on question cards
 * @param onNext – shows a "Next" button at the bottom (leave out while editing)
 * @param locked – the card must be answered before moving on
 */
export const CourseCard = ({
    editable,
    card,
    cardResult,
    busyAnswering,
    onAnswer,
    showing,
    onClickText,
    onClickOption,
    coverUrl,
    questionNumber,
    questionCount,
    onNext,
    locked
}) => {
    const { intro, text, options, file } = card || {};
    const [hideContent, setHideContent] = useState(false);
    // The question is shown in full before its options appear, so they can't be answered unread
    const [revealed, setRevealed] = useState(false);
    const isQuestion = options?.length > 0;
    const hasContent = isQuestion || !!text;
    // Text or question on top of an image can be hidden to see the whole image
    const canHideContent = !!file && hasContent && !editable;

    // "plain": text on the blurred cover, "overlay": text on top of the card's image, "only": just the image
    const layout = !file ? "plain" : hasContent && !hideContent ? "overlay" : "only";

    const questionMeta =
        isQuestion && questionNumber ? (
            <p className={styles.meta}>
                <FormattedMessage
                    id={`${msgKey}.questionOf`}
                    values={{ number: questionNumber, count: questionCount }}
                />
            </p>
        ) : null;

    // Start with the text shown (and revealed again) each time the card comes back into view
    useEffect(() => {
        if (!showing) {
            setHideContent(false);
            setRevealed(false);
        }
    }, [showing]);

    return (
        <div
            className={classNames(styles.base, styles[layout], {
                [styles.editable]: editable
            })}
        >
            <div className={styles.background}>
                {file ? (
                    <Image src={file.url} size="fit" squared />
                ) : (
                    <img src={coverUrl || coverImage} alt="" className={styles.blurred} />
                )}
            </div>

            {showing && (
                <div className={styles.body}>
                    {hasContent && !hideContent && (
                        <div className={styles.content}>
                            {intro && text ? (
                                <p className={styles.intro} onClick={editable ? onClickText : null}>
                                    {intro}
                                </p>
                            ) : null}

                            {questionMeta}

                            {text ? (
                                <div
                                    className={classNames(styles.text, {
                                        [styles.question]: isQuestion,
                                        [styles.lead]: !isQuestion
                                    })}
                                    onClick={editable ? onClickText : null}
                                >
                                    {!!cardResult || editable ? (
                                        text
                                    ) : (
                                        <TextReveal text={text} onDone={() => setRevealed(true)} />
                                    )}
                                </div>
                            ) : null}

                            {isQuestion ? (
                                <div
                                    className={classNames(styles.options, {
                                        [styles.answered]: !!cardResult,
                                        [styles.editable]: editable,
                                        [styles.waiting]: !revealed && !!text && !cardResult && !editable
                                    })}
                                >
                                    {(editable ? sortById(options) : cardResult?.options || options).map((option) => (
                                        <OptionButton
                                            key={option.id}
                                            option={option}
                                            answered={!!cardResult}
                                            busy={busyAnswering}
                                            editable={editable}
                                            onSelect={() => (editable ? onClickOption(option) : onAnswer(option))}
                                        />
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    )}

                    {(onNext || canHideContent) && !editable ? (
                        <div className={styles.foot}>
                            {canHideContent ? (
                                <button
                                    className={styles.peek}
                                    onClick={() => setHideContent(!hideContent)}
                                    aria-pressed={hideContent}
                                >
                                    <Icon name={hideContent ? "eye" : "image"} />
                                    <FormattedMessage
                                        id={
                                            !hideContent
                                                ? `${msgKey}.showImage`
                                                : isQuestion
                                                  ? `${msgKey}.showQuestion`
                                                  : `${msgKey}.showText`
                                        }
                                    />
                                </button>
                            ) : null}
                            {onNext ? (
                                <Button
                                    fill
                                    size="big"
                                    color="primary"
                                    icon={locked ? null : "arrow-right"}
                                    iconRight
                                    disabled={locked}
                                    onClick={onNext}
                                >
                                    <FormattedMessage id={locked ? `${msgKey}.chooseAnswer` : `${msgKey}.next`} />
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            )}

            {/* The other cards' content too, so the whole course is in the page (e.g. for search engines). Plain,
                with nothing to click or answer: the card gets its interactive content when it's shown. */}
            {!showing && !editable && hasContent && (
                <div className={styles.body}>
                    <div className={styles.content}>
                        {intro && text ? <p className={styles.intro}>{intro}</p> : null}
                        {questionMeta}
                        {text ? (
                            <div
                                className={classNames(styles.text, {
                                    [styles.question]: isQuestion,
                                    [styles.lead]: !isQuestion
                                })}
                            >
                                {text}
                            </div>
                        ) : null}
                        {isQuestion ? (
                            <ul className={classNames(styles.options, styles.list)}>
                                {options.map((option) => (
                                    <li key={option.id} className={styles.option}>
                                        <span className={styles.dot} aria-hidden="true" />
                                        <span className={styles.label}>{option.text}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : null}
                    </div>
                </div>
            )}
        </div>
    );
};

// A sorted copy: sorting the options in place on every render, with a comparator that returns `false` when not
// editing, reversed them on each render in Safari/iOS (WebKit), so the answer buttons jumped around
const sortById = (options) => [...options].sort((a, b) => a.id - b.id);

const OptionButton = ({ option, onSelect, answered, editable, busy }) => {
    // Optimistic instant click feedback: Temp selected is used to show a spinner while the answer is being checked
    const [tempSelected, setTempSelected] = useState(null);
    const { selected, correct } = option;

    useEffect(() => {
        if (!busy || selected) {
            setTempSelected(null);
        }
    }, [selected, busy]);

    // right: the correct answer, wrong: picked but not correct, dim: the rest once answered
    const state = editable
        ? correct
            ? "right"
            : "idle"
        : !answered
          ? "idle"
          : correct
            ? "right"
            : selected
              ? "wrong"
              : "dim";

    return (
        <button
            className={classNames(styles.option, styles[state], {
                [styles.pending]: busy && tempSelected
            })}
            onClick={() => {
                if (!editable && !answered) {
                    setTempSelected(true);
                }
                onSelect();
            }}
            disabled={busy || (!editable && answered)}
        >
            <span className={styles.dot} aria-hidden="true">
                {busy && tempSelected ? (
                    <Spinner size="fill" color="contrast" />
                ) : state === "right" ? (
                    <Icon name="check" />
                ) : state === "wrong" ? (
                    <Icon name="close" />
                ) : null}
            </span>
            <span className={styles.label}>{option.text}</span>
        </button>
    );
};
